import React, { useEffect, useState } from 'react';
import './PickAccount.css';
import { useNavigate } from 'react-router-dom';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { getEnvironmentParams } from '../utils/auth';
import { apiUrl } from '../config/envConfig';
import PageLayout from './common/PageLayout';
import { useTranslation } from 'react-i18next';
import { returnWithBcStart } from '../utils/returnToHost';

// HTTP status codes that imply the consent or session can't be saved — we
// can't recover by retrying, so send the user back to start a fresh consent.
const UNRECOVERABLE_STATUSES = new Set([401, 403, 410]);

const currencySymbols = {
    SEK: 'kr',
    EUR: '€',
    USD: '$',
    GBP: '£',
};

function getCurrencySymbol(currency) {
    return currencySymbols[currency] || currency;
}

function PickAccount() {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const [accounts, setAccounts] = useState([]);
    // Ids of the selected accounts, in the order they were checked. Exactly one
    // of them (primaryId) is the designated primary account.
    const [selectedIds, setSelectedIds] = useState([]);
    const [primaryId, setPrimaryId] = useState(null);
    const [acceptedTerms, setAcceptedTerms] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isSelectingAccount, setIsSelectingAccount] = useState(false);

    const consentData = JSON.parse(sessionStorage.getItem('consentData'));
    const bankingInfo = JSON.parse(sessionStorage.getItem('selectedBank'));
    const [userIp, setUserIp] = useState('');
    const [accountsFetched, setAccountsFetched] = useState(false);

    useEffect(() => {
        fetch('https://api.ipify.org?format=json')
            .then(res => res.json())
            .then(data => setUserIp(data.ip))
            .catch(() => setUserIp('127.0.0.1'));
    }, []);

    const fetchAccounts = async () => {
        try {
            setError(null);
            setLoading(true);
            const params = getEnvironmentParams();
            if (!params.token) {
                throw new Error('No authentication token found');
            }
            const headers = {
                'accept': 'application/json',
                'Consent-Id': consentData?.consentId,
                'BicFi': bankingInfo?.bicFi,
                'Psu-Ip-Address': userIp,
                'Psu-Id': bankingInfo?.socialSecurityNumber,
                'Authorization': `Bearer ${params.token}`,
                'Tpp-Redirect-Preferred': true
            };
            // Affiliated banks are keyed by the affiliate id, not BicFi alone — the hub
            // refuses to guess the affiliate and 404s without it. Same value as the
            // AspspsId sent at consent creation.
            const affiliatedAspspId = bankingInfo?.selectedOption?.id;
            if (affiliatedAspspId) {
                headers['Affiliated-Aspsp-Id'] = affiliatedAspspId;
            }
            const response = await fetch(`${apiUrl}/accounts`, {
                method: 'GET',
                headers: headers
            });
            if (!response.ok) {
                if (UNRECOVERABLE_STATUSES.has(response.status)) {
                    // Token / consent is gone — restart the consent flow on the host side.
                    returnWithBcStart('/aspspsList');
                    return;
                }
                throw new Error(`HTTP ${response.status}`);
            }
            const data = await response.json();
            const accountList = Array.isArray(data) ? data : (data.accounts || []);
            setAccounts(accountList);

            // Accounts already stored on the consent (a reused consent lands here
            // directly) come back flagged isSelected / isPrimary — map them into the
            // selection state so the previous choice is pre-checked.
            const preselectedIds = accountList
                .filter((account) => account.isSelected)
                .map((account) => account.id);
            if (preselectedIds.length > 0) {
                setSelectedIds(preselectedIds);
                const primaryAccount = accountList.find(
                    (account) => account.isPrimary && account.isSelected
                );
                // Same fallback as manual picking: no primary flagged -> first selected.
                setPrimaryId(primaryAccount ? primaryAccount.id : preselectedIds[0]);
            }
        } catch (err) {
            console.error('Error loading accounts:', err);
            setError(t('pickLoadAccountsError', 'Could not load accounts. Please try again.'));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (
            !accountsFetched &&
            consentData?.consentId &&
            bankingInfo?.bicFi &&
            userIp
        ) {
            setAccountsFetched(true);
            fetchAccounts();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [accountsFetched, consentData, bankingInfo, userIp]);

    const handleAccountToggle = (accountId) => {
        if (selectedIds.includes(accountId)) {
            const remaining = selectedIds.filter(id => id !== accountId);
            setSelectedIds(remaining);
            // If the primary account is deselected, the first remaining
            // selected account (in check order) becomes the new primary.
            if (primaryId === accountId) {
                setPrimaryId(remaining.length > 0 ? remaining[0] : null);
            }
        } else {
            setSelectedIds([...selectedIds, accountId]);
            // Default primary = first account checked.
            if (primaryId === null) {
                setPrimaryId(accountId);
            }
        }
    };

    const handlePrimarySelect = (event, accountId) => {
        // Don't let the click bubble up and toggle the row's selection.
        event.stopPropagation();
        if (selectedIds.includes(accountId)) {
            setPrimaryId(accountId);
        }
    };

    const handleNextClick = async () => {
        if (isSelectingAccount || selectedIds.length === 0 || primaryId === null) {
            return;
        }

        setIsSelectingAccount(true);
        setError(null);

        try {
            const params = getEnvironmentParams();
            if (!params.token) {
                throw new Error('No authentication token found');
            }

            // The multi-account endpoint (ConsentsController.UpdateConsentWithAccountsAsync)
            // binds no request headers — the handler resolves BicFi / Psu-Id / Psu-Ip-Address
            // from the stored consent record — so only auth and content negotiation are sent.
            const headers = {
                'accept': 'application/json',
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${params.token}`,
            };

            // The hub's GET /accounts returns ApiAccountDto objects:
            // { id, name, accountNumber, balance, currency } (camelCase). `id` carries the
            // provider-side resourceId (AccountProfile maps ResourceId -> Id in the hub), so
            // `account.id` is the correct identifier for the account ids in the body. There
            // is NO `resourceId` property on the payload the FE receives.
            // The selected ids travel in the request body of the multi-account endpoint
            // (ConsentsController.UpdateConsentWithAccountsAsync); they are no longer part
            // of the URL path.
            const response = await fetch(
                `${apiUrl}/consents/${consentData.consentId}/accounts`,
                {
                    method: 'PUT',
                    headers: headers,
                    body: JSON.stringify({
                        accountIds: selectedIds,
                        primaryAccountId: primaryId
                    })
                }
            );

            if (!response.ok) {
                if (UNRECOVERABLE_STATUSES.has(response.status)) {
                    returnWithBcStart('/aspspsList');
                    return;
                }
                throw new Error(`HTTP ${response.status}`);
            }

            // Override the fetched isSelected/isPrimary flags with the user's final
            // choice — the stored data must reflect what was just PUT, not what the
            // backend returned on load.
            const selectedAccounts = selectedIds
                .map(id => accounts.find(account => account.id === id))
                .filter(Boolean)
                .map(account => ({ ...account, isSelected: true, isPrimary: account.id === primaryId }));

            sessionStorage.setItem('selectedAccountsData', JSON.stringify(selectedAccounts));
            navigate('/summary');
        } catch (err) {
            console.error('Error selecting accounts:', err);
            setError(t('pickSelectAccountError', 'Could not save your account selection. Please try again.'));
        } finally {
            setIsSelectingAccount(false);
        }
    };

    const handleRetryFetch = () => {
        setError(null);
        setAccountsFetched(false);
    };

    const handleBackToStart = () => {
        returnWithBcStart('/aspspsList');
    };

    const renderAccountsContent = () => {
        if (loading) {
            return <div>{t('pickLoading')}</div>;
        }
        if (error && accounts.length === 0) {
            return (
                <div>
                    <div className="error-message">{error}</div>
                    <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                        <button type="button" onClick={handleRetryFetch}>
                            {t('pickRetry', 'Try again')}
                        </button>
                        <button type="button" onClick={handleBackToStart}>
                            {t('pickBackToStart', 'Back to start')}
                        </button>
                    </div>
                </div>
            );
        }
        if (accounts.length === 0) {
            return <div className="no-accounts-message">{t('pickNoAccounts')}</div>;
        }
        return (
            <div className="accounts-list">
                {accounts.map((account) => {
                    const isSelected = selectedIds.includes(account.id);
                    const isPrimary = primaryId === account.id;
                    return (
                        <div
                            key={`${account.name}-${account.balance}-${account.currency}`}
                            className={`account-item ${isSelected ? 'selected' : ''}`}
                            onClick={() => handleAccountToggle(account.id)}
                        >
                            <div className="account-info">
                                <div className={`account-checkbox ${isSelected ? 'checked' : ''}`}>
                                    {isSelected && <span className="account-checkbox-mark">✓</span>}
                                </div>
                                <div className="account-details">
                                    <span className="account-type">{account.name}</span>
                                    <span className="account-number">····{account.accountNumber || ''}</span>
                                </div>
                            </div>
                            <div className="account-item-right">
                                {isSelected && (
                                    <button
                                        type="button"
                                        className={`primary-toggle ${isPrimary ? 'is-primary' : ''}`}
                                        onClick={(e) => handlePrimarySelect(e, account.id)}
                                        aria-pressed={isPrimary}
                                    >
                                        <span className={`primary-radio ${isPrimary ? 'checked' : ''}`}>
                                            {isPrimary && <span className="primary-radio-dot" />}
                                        </span>
                                        {t('pickPrimary', 'Primary')}
                                    </button>
                                )}
                                <div className="account-balance">
                                    <span>
                                        {Number(account.balance).toLocaleString()} {getCurrencySymbol(account.currency)}
                                    </span>
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
        );
    };

    const navigationProps = {
        previousPath: "/bank-connected",
        onNext: handleNextClick,
        nextDisabled: selectedIds.length === 0 || !acceptedTerms || isSelectingAccount
    };

    return (
        <PageLayout
            currentStep={4}
            navigationProps={navigationProps}
            infoText="pickInfoText"
        >
            <div className="pick-account-content">
                <h1>{t('pickTitle')}</h1>
                <p className="subtitle">{t('pickSubtitle')}</p>

                {renderAccountsContent()}

                {error && accounts.length > 0 && (
                    <div className="error-message" style={{ marginTop: 12 }}>
                        {error}
                    </div>
                )}

                {isSelectingAccount && (
                    <div className="selecting-account-message">
                        {t('pickSelectingAccount')}
                    </div>
                )}

                <div className="terms-section">
                    <h4 className="terms-heading">{t('pickTermsHeading')}</h4>
                    <label className="terms-checkbox">
                        <input
                            type="checkbox"
                            checked={acceptedTerms}
                            onChange={(e) => setAcceptedTerms(e.target.checked)}
                        />
                        <span>
                            {t('pickAcceptPrefix')}{' '}
                            <a href="/terms-full" target="_blank">{t('pickTerms')}</a>
                            {' '}{t('pickAnd')}{' '}
                            <a href="/privacy-policy" target="_blank">{t('pickPrivacy')} <OpenInNewIcon fontSize="small" /></a>
                            {' '}{t('pickAuthorisePayments')}
                        </span>
                    </label>
                </div>
            </div>
        </PageLayout>
    );
}

export default PickAccount;
